<template>
  <CardExpandable v-if="items.length > 0" :loading="isLoading">
    <template #title>
      <div class="expandable-title text-medium-emphasis">
        {{ t('deeppay.payments_history.card_expandable.title') }}
      </div>
      <v-spacer />
    </template>
    <template #content>
      <DeepPayPaymentsHistoryList
        :box-id="boxId"
        :items="items"
        :origin-organization-id="originOrganizationId"
        :type-id="typeId"
      />
    </template>
  </CardExpandable>
</template>

<script setup lang="ts">
import { toast } from 'vue-sonner'
import { MIME_TYPES } from '@deepcloud/deep-ui-utils'
import { useApi } from '@/composables/api'
import CardExpandable from '@/components/cards/CardExpandable.vue'
import DeepPayPaymentsHistoryList from './DeepPayPaymentsHistoryList.vue'
import type { PaymentHistory } from '@deepcloud/api-deep-o'

const props = defineProps({
  originOrganizationId: {
    type: String,
    required: true,
  },
  typeId: {
    type: String,
    required: true,
  },
  boxId: {
    type: String,
    required: true,
  },
  nodeId: {
    type: String,
    required: true,
  },
  mimeType: {
    type: String,
    required: true,
  },
})

const items = ref<PaymentHistory[]>([])
const { t } = useI18n()
const { deepOAPI } = useApi()

const isLoading = ref(false)

async function fetchPaymentsHistory() {
  isLoading.value = true
  try {
    const { data } = await deepOAPI.value.paymentsHistoryDocumentsPayments.get(
      props.nodeId,
      {
        companyId: props.originOrganizationId,
        deepBoxNodeId: props.typeId,
        boxNodeId: props.boxId,
      },
    )
    items.value = data.payments
    sortSource(items.value, 'createdAt', 'desc')
  } catch {
    toast.error(t('error.error_occurred'))
  } finally {
    isLoading.value = false
  }
}

watch(
  () => props.nodeId,
  () => {
    items.value = []
    if (
      [
        MIME_TYPES.MIME_TYPE_PDF,
        MIME_TYPES.MIME_TYPE_DEEPPAY_PAYMENT_ORDER,
      ].includes(props.mimeType)
    ) {
      fetchPaymentsHistory()
    }
  },
  {
    immediate: true,
  },
)

function sortSource(
  source: PaymentHistory[],
  sortField: string,
  sortOrder: 'asc' | 'desc',
) {
  source.sort((a, b) => {
    if (sortOrder === 'asc') {
      return a[sortField]?.localeCompare(b[sortField])
    } else {
      return -a[sortField]?.localeCompare(b[sortField])
    }
  })
}
</script>

<style scoped lang="scss">
.expandable-title {
  font-size: 13px;
}
</style>
